import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { useTenantStore } from '../stores/tenant.store';
import { treasuryViewFromPath, type TreasuryViewId } from './treasury/treasuryRoutes';
import { TreasuryPositionDashboard } from './treasury/TreasuryPositionDashboard';
import {
  formatMoney,
  registerTypeLabel,
  transferStatusLabel,
  directionLabel,
  humanizeTreasuryError,
} from './treasury/treasuryUi';

type Tab = 'summary' | 'registers' | 'transfers' | 'cashBank' | 'adjustments' | 'policy' | 'approvals';

function viewToTab(view: TreasuryViewId): Tab {
  switch (view) {
    case 'position':
      return 'summary';
    case 'transfers':
      return 'transfers';
    case 'approvals':
      return 'approvals';
    case 'cash-registers':
    case 'supervision':
      return 'summary';
    case 'config':
      return 'policy';
    case 'move':
    case 'banks':
      return 'summary';
    default:
      return 'summary';
  }
}

export function TreasuryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = treasuryViewFromPath(location.pathname);
  const tab = viewToTab(view);
  const focusParam = searchParams.get('focus');
  const directionParam = searchParams.get('direction');
  const [fundsSubTab, setFundsSubTab] = useState<'structural' | 'bank'>(() =>
    focusParam === 'bank' ? 'bank' : 'structural',
  );
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const [summary, setSummary] = useState<any>(null);
  const [registers, setRegisters] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [cashBankTransfers, setCashBankTransfers] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transferForm, setTransferForm] = useState({
    fromRegisterId: '',
    toRegisterId: '',
    amount: '',
    notes: '',
  });
  const [adjustForm, setAdjustForm] = useState({
    cashRegisterId: '',
    direction: 'IN' as 'IN' | 'OUT',
    amount: '',
    reason: '',
  });
  const [policyForm, setPolicyForm] = useState({
    transferApprovalThreshold: '100000',
    allowSelfApproval: false,
    allowOperativeDirectTransfer: true,
  });

  const load = async () => {
    if (!legalEntityId) return;
    setLoading(true);
    setError('');
    try {
      if (tab === 'summary') setSummary(await apiService.getTreasurySummary(legalEntityId));
      if (tab === 'registers') {
        setRegisters(await apiService.getCashRegisters(undefined, legalEntityId));
      }
      if (tab === 'transfers' || tab === 'approvals') {
        const [regs, tr, cbt, adj] = await Promise.all([
          apiService.getCashRegisters(undefined, legalEntityId),
          apiService.listTreasuryTransfers(legalEntityId),
          apiService.listCashBankTransfers(legalEntityId),
          apiService.listTreasuryAdjustments(legalEntityId),
        ]);
        setRegisters(regs);
        setTransfers(tr);
        setCashBankTransfers(cbt);
        setAdjustments(adj);
      }
      if (tab === 'cashBank') {
        const [regs, cbt] = await Promise.all([
          apiService.getCashRegisters(undefined, legalEntityId),
          apiService.listCashBankTransfers(legalEntityId),
        ]);
        setRegisters(regs);
        setCashBankTransfers(cbt);
      }
      if (tab === 'adjustments') {
        setAdjustments(await apiService.listTreasuryAdjustments(legalEntityId));
        setRegisters(await apiService.getCashRegisters(undefined, legalEntityId));
      }
      // config (policy) also needs registers + adjustments for sobrantes/faltantes
      if (tab === 'policy') {
        const [p, regs, adj] = await Promise.all([
          apiService.getTreasuryPolicy(legalEntityId),
          apiService.getCashRegisters(undefined, legalEntityId),
          apiService.listTreasuryAdjustments(legalEntityId),
        ]);
        setPolicy(p);
        setRegisters(regs);
        setAdjustments(adj);
        setPolicyForm({
          transferApprovalThreshold: String(p.transferApprovalThreshold ?? 100000),
          allowSelfApproval: !!p.allowSelfApproval,
          allowOperativeDirectTransfer: p.allowOperativeDirectTransfer !== false,
        });
      }
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message || 'Error al cargar'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntityId, tab, fundsSubTab]);

  useEffect(() => {
    if (focusParam === 'bank') setFundsSubTab('bank');
    if (directionParam === 'DEPOSIT') {
      navigate('/treasury/move?type=deposit', { replace: true });
    } else if (directionParam === 'WITHDRAWAL') {
      navigate('/treasury/move?type=withdrawal', { replace: true });
    }
  }, [focusParam, directionParam, navigate]);

  useEffect(() => {
    if (tab !== 'registers' || !focusParam || focusParam === 'bank') return;
    const el = document.getElementById(`register-${focusParam}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [tab, focusParam, registers]);

  const pendingTransfers = useMemo(
    () => transfers.filter((t) => t.status === 'PENDING'),
    [transfers],
  );
  const pendingCashBank = useMemo(
    () => cashBankTransfers.filter((t) => t.status === 'PENDING'),
    [cashBankTransfers],
  );
  const pendingAdjustments = useMemo(
    () => adjustments.filter((a) => a.status === 'PENDING'),
    [adjustments],
  );

  const pageTitle: Record<TreasuryViewId, string> = {
    position: 'Posición de tesorería',
    move: 'Mover fondos',
    transfers: 'Movimientos de fondos',
    approvals: 'Aprobaciones',
    banks: 'Bancos',
    supervision: 'Supervisión',
    'cash-registers': 'Cajas',
    config: 'Configuración',
  };

  if (tab === 'summary') {
    return (
      <TreasuryPositionDashboard
        summary={summary}
        loading={loading}
        error={error}
        legalEntityId={legalEntityId}
        onRefresh={() => void load()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{pageTitle[view]}</h2>
          <p className="text-sm text-slate-600 mt-1">
            {view === 'transfers' &&
              'Mové fondos entre registros de tesorería o registrá depósitos y extracciones bancarias.'}
            {view === 'approvals' && 'Cola de transferencias y ajustes pendientes de aprobación.'}
            {view === 'cash-registers' &&
              'Inventario de registros (Central / Madre / Operativa). No es la sesión del cajero.'}
            {view === 'config' &&
              'Políticas de movimientos de fondos y sobrantes/faltantes. Las cajas se administran en Cajas.'}{' '}
            <Link to="/cash" className="text-blue-700 hover:underline">
              Ir a Mi caja
            </Link>
            {view === 'config' && (
              <>
                {' · '}
                <Link to="/treasury/cash-registers" className="text-blue-700 hover:underline">
                  Ir a Cajas
                </Link>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading || !legalEntityId}
          className="px-3 py-2 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          Actualizar
        </button>
      </div>

      {!legalEntityId && (
        <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
          Seleccioná una empresa en la barra de contexto.
        </p>
      )}
      {error && (
        <p className="text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">{error}</p>
      )}

      {tab === 'registers' && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2">Caja</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Sucursal</th>
                <th className="px-3 py-2">Moneda</th>
                <th className="px-3 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {registers.map((r) => (
                <tr
                  key={r.id}
                  id={`register-${r.id}`}
                  className={`border-t ${focusParam === r.id ? 'bg-sky-50 ring-1 ring-inset ring-sky-200' : ''}`}
                >
                  <td className="px-3 py-2">
                    {r.name || r.code} ({r.number})
                  </td>
                  <td className="px-3 py-2">{registerTypeLabel(r.type)}</td>
                  <td className="px-3 py-2">{r.branch?.name || '—'}</td>
                  <td className="px-3 py-2">{r.currency || 'ARS'}</td>
                  <td className="px-3 py-2">
                    {r.sessions?.[0] ? 'Sesión abierta' : r.isActive ? 'Activa' : 'Inactiva'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'transfers' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFundsSubTab('structural')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                fundsSubTab === 'structural' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'
              }`}
            >
              Entre Central / Madre / Operativas
            </button>
            <button
              type="button"
              onClick={() => setFundsSubTab('bank')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                fundsSubTab === 'bank' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'
              }`}
            >
              Depósitos y extracciones
            </button>
            </div>
            <Link
              to="/treasury/move"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
            >
              + Mover fondos
            </Link>
          </div>

          {fundsSubTab === 'structural' && (
          <div className="space-y-6">
          <form
            className="grid md:grid-cols-2 gap-3 border rounded-lg p-4 bg-white"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await apiService.requestTreasuryTransfer({
                  fromRegisterId: transferForm.fromRegisterId,
                  toRegisterId: transferForm.toRegisterId,
                  amount: parseFloat(transferForm.amount),
                  notes: transferForm.notes || undefined,
                });
                setTransferForm({ fromRegisterId: '', toRegisterId: '', amount: '', notes: '' });
                await load();
              } catch (err: any) {
                setError(humanizeTreasuryError(err?.response?.data?.message || err.message));
              }
            }}
          >
            <h2 className="md:col-span-2 font-medium">Movimiento de fondos</h2>
            <p className="md:col-span-2 text-xs text-slate-500">
              Mové fondos entre Tesorería Central, cajas madre y cajas operativas. No es el pase entre
              cajeros con sesión abierta (eso está en Mi caja).
            </p>
            <select
              required
              className="border rounded-md px-3 py-2"
              value={transferForm.fromRegisterId}
              onChange={(e) => setTransferForm({ ...transferForm, fromRegisterId: e.target.value })}
            >
              <option value="">Origen</option>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {registerTypeLabel(r.type)} · {r.name || r.code}
                </option>
              ))}
            </select>
            <select
              required
              className="border rounded-md px-3 py-2"
              value={transferForm.toRegisterId}
              onChange={(e) => setTransferForm({ ...transferForm, toRegisterId: e.target.value })}
            >
              <option value="">Destino</option>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {registerTypeLabel(r.type)} · {r.name || r.code}
                </option>
              ))}
            </select>
            <input
              required
              type="number"
              step="0.01"
              min="0.01"
              placeholder="Monto"
              className="border rounded-md px-3 py-2"
              value={transferForm.amount}
              onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
            />
            <input
              placeholder="Observaciones"
              className="border rounded-md px-3 py-2"
              value={transferForm.notes}
              onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
            />
            <button type="submit" className="md:col-span-2 px-3 py-2 bg-slate-900 text-white rounded-md text-sm">
              Confirmar
            </button>
          </form>

          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Origen</th>
                  <th className="px-3 py-2">Destino</th>
                  <th className="px-3 py-2">Monto</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="px-3 py-2">{new Date(t.createdAt).toLocaleString('es-AR')}</td>
                    <td className="px-3 py-2">{t.fromRegister?.code}</td>
                    <td className="px-3 py-2">{t.toRegister?.code}</td>
                    <td className="px-3 py-2">{formatMoney(Number(t.amount))}</td>
                    <td className="px-3 py-2">{transferStatusLabel(t.status)}</td>
                    <td className="px-3 py-2 space-x-2">
                      {t.status === 'PENDING' && (
                        <>
                          <button
                            type="button"
                            className="text-green-700 hover:underline"
                            onClick={async () => {
                              await apiService.approveTreasuryTransfer(t.id);
                              await load();
                            }}
                          >
                            Aprobar
                          </button>
                          <button
                            type="button"
                            className="text-red-700 hover:underline"
                            onClick={async () => {
                              await apiService.rejectTreasuryTransfer(t.id);
                              await load();
                            }}
                          >
                            Rechazar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
          )}

          {fundsSubTab === 'bank' && (
        <div className="space-y-4">
          <div className="border rounded-lg p-4 bg-white space-y-2">
            <h2 className="font-medium text-slate-900">Depósitos y extracciones</h2>
            <p className="text-sm text-slate-600">
              Registrá depósitos y extracciones desde{' '}
              <Link to="/treasury/banks" className="text-blue-700 hover:underline">
                Tesorería → Bancos
              </Link>
              . Ahí también se administran los vínculos cuenta ↔ Central / Madre.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to="/treasury/banks"
                className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded-md"
              >
                Ir a Bancos
              </Link>
              <Link
                to="/treasury/move?type=deposit"
                className="px-3 py-1.5 text-sm border rounded-md hover:bg-slate-50"
              >
                Depositar
              </Link>
              <Link
                to="/treasury/move?type=withdrawal"
                className="px-3 py-1.5 text-sm border rounded-md hover:bg-slate-50"
              >
                Retirar
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Dirección</th>
                  <th className="px-3 py-2">Caja</th>
                  <th className="px-3 py-2">Banco</th>
                  <th className="px-3 py-2">Monto</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cashBankTransfers.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="px-3 py-2">
                      {new Date(t.requestedAt || t.createdAt).toLocaleString('es-AR')}
                    </td>
                    <td className="px-3 py-2">{directionLabel(t.direction)}</td>
                    <td className="px-3 py-2">{t.cashRegister?.code}</td>
                    <td className="px-3 py-2">{t.bankAccount?.bankName}</td>
                    <td className="px-3 py-2">{formatMoney(Number(t.amount))}</td>
                    <td className="px-3 py-2">{transferStatusLabel(t.status)}</td>
                    <td className="px-3 py-2 space-x-2">
                      {t.status === 'PENDING' && (
                        <>
                          <button
                            type="button"
                            className="text-green-700 hover:underline"
                            onClick={async () => {
                              await apiService.approveCashBankTransfer(t.id);
                              await load();
                            }}
                          >
                            Aprobar
                          </button>
                          <button
                            type="button"
                            className="text-red-700 hover:underline"
                            onClick={async () => {
                              await apiService.rejectCashBankTransfer(t.id);
                              await load();
                            }}
                          >
                            Rechazar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          )}
        </div>
      )}

      {tab === 'approvals' && (
        <div className="space-y-6">
          <section className="border rounded-lg p-4 bg-white space-y-3">
            <h3 className="font-medium text-slate-900">Movimientos de fondos pendientes</h3>
            <p className="text-xs text-slate-500">
              Transferencias estructurales entre Central, Madres y Operativas.
            </p>
            {pendingTransfers.length === 0 ? (
              <p className="text-sm text-slate-500">No hay pendientes.</p>
            ) : (
              <ul className="divide-y text-sm">
                {pendingTransfers.map((t) => (
                  <li key={t.id} className="py-2 flex flex-wrap justify-between gap-2">
                    <span>
                      {formatMoney(Number(t.amount))} · {t.fromRegister?.code || t.fromRegisterId} →{' '}
                      {t.toRegister?.code || t.toRegisterId}
                    </span>
                    <span className="flex gap-2">
                      <button
                        type="button"
                        className="text-green-700 hover:underline"
                        onClick={async () => {
                          await apiService.approveTreasuryTransfer(t.id);
                          await load();
                        }}
                      >
                        Aprobar
                      </button>
                      <button
                        type="button"
                        className="text-red-700 hover:underline"
                        onClick={async () => {
                          await apiService.rejectTreasuryTransfer(t.id);
                          await load();
                        }}
                      >
                        Rechazar
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="border rounded-lg p-4 bg-white space-y-3">
            <h3 className="font-medium text-slate-900">Depósitos / extracciones pendientes</h3>
            {pendingCashBank.length === 0 ? (
              <p className="text-sm text-slate-500">No hay pendientes.</p>
            ) : (
              <ul className="divide-y text-sm">
                {pendingCashBank.map((t) => (
                  <li key={t.id} className="py-2 flex flex-wrap justify-between gap-2">
                    <span>
                      {directionLabel(t.direction)} · {formatMoney(Number(t.amount))} · {t.cashRegister?.code}
                    </span>
                    <span className="flex gap-2">
                      <button
                        type="button"
                        className="text-green-700 hover:underline"
                        onClick={async () => {
                          await apiService.approveCashBankTransfer(t.id);
                          await load();
                        }}
                      >
                        Aprobar
                      </button>
                      <button
                        type="button"
                        className="text-red-700 hover:underline"
                        onClick={async () => {
                          await apiService.rejectCashBankTransfer(t.id);
                          await load();
                        }}
                      >
                        Rechazar
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="border rounded-lg p-4 bg-white space-y-3">
            <h3 className="font-medium text-slate-900">Sobrantes / faltantes pendientes</h3>
            {pendingAdjustments.length === 0 ? (
              <p className="text-sm text-slate-500">No hay pendientes.</p>
            ) : (
              <ul className="divide-y text-sm">
                {pendingAdjustments.map((a) => (
                  <li key={a.id} className="py-2 flex flex-wrap justify-between gap-2">
                    <span>
                      {a.direction === 'IN' ? 'Sobrante' : 'Faltante'} · {formatMoney(Number(a.amount))} ·{' '}
                      {a.cashRegister?.code} · {a.reason}
                    </span>
                    {a.status === 'PENDING' && (
                      <button
                        type="button"
                        className="text-green-700 hover:underline"
                        onClick={async () => {
                          await apiService.approveTreasuryAdjustment(a.id);
                          await load();
                        }}
                      >
                        Aprobar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}


      {tab === 'policy' && (
        <div className="space-y-8">
          {policy && (
            <form
              className="space-y-3 border rounded-lg p-4 max-w-md bg-white"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await apiService.updateTreasuryPolicy(legalEntityId!, {
                    transferApprovalThreshold: parseFloat(policyForm.transferApprovalThreshold),
                    allowSelfApproval: policyForm.allowSelfApproval,
                    allowOperativeDirectTransfer: policyForm.allowOperativeDirectTransfer,
                  });
                  await load();
                } catch (err: any) {
                  setError(humanizeTreasuryError(err?.response?.data?.message || err.message));
                }
              }}
            >
              <h2 className="font-medium text-slate-900">Movimientos de fondos</h2>
              <p className="text-xs text-slate-500">
                Reglas para transferencias entre Central, Madre y Operativas (no aplica a Mi caja).
              </p>
              <label className="block text-sm">
                Monto a partir del cual se requiere aprobación
                <input
                  type="number"
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={policyForm.transferApprovalThreshold}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, transferApprovalThreshold: e.target.value })
                  }
                />
                <span className="block text-xs text-slate-500 mt-1">
                  Por debajo de este monto el movimiento puede ejecutarse sin cola de aprobación
                  (según las otras reglas).
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={policyForm.allowSelfApproval}
                  onChange={(e) => setPolicyForm({ ...policyForm, allowSelfApproval: e.target.checked })}
                />
                <span>
                  Permitir que quien solicita también apruebe
                  <span className="block text-xs text-slate-500">
                    Si está apagado, otro usuario con permiso debe aprobar.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={policyForm.allowOperativeDirectTransfer}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, allowOperativeDirectTransfer: e.target.checked })
                  }
                />
                <span>
                  Permitir transferencias directas entre cajas operativas
                  <span className="block text-xs text-slate-500">
                    Si está apagado, el efectivo debe pasar por Caja Madre.
                  </span>
                </span>
              </label>
              <button type="submit" className="px-3 py-2 bg-slate-900 text-white rounded-md text-sm">
                Guardar política
              </button>
            </form>
          )}

          <div className="space-y-6">
            <form
              className="grid md:grid-cols-2 gap-3 border rounded-lg p-4 bg-white"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await apiService.createTreasuryAdjustment({
                    cashRegisterId: adjustForm.cashRegisterId,
                    direction: adjustForm.direction,
                    amount: parseFloat(adjustForm.amount),
                    reason: adjustForm.reason,
                  });
                  setAdjustForm({ cashRegisterId: '', direction: 'IN', amount: '', reason: '' });
                  await load();
                } catch (err: any) {
                  setError(humanizeTreasuryError(err?.response?.data?.message || err.message));
                }
              }}
            >
              <h2 className="md:col-span-2 font-medium">Sobrantes y faltantes</h2>
              <p className="md:col-span-2 text-xs text-slate-500">
                Solicitudes de sobrante (+) o faltante (−). Estados: Pendiente · Aprobado · Rechazado.
                El asiento contable lo genera el backend al aprobar; no se contabiliza a mano.
              </p>
              <select
                required
                className="border rounded-md px-3 py-2"
                value={adjustForm.cashRegisterId}
                onChange={(e) => setAdjustForm({ ...adjustForm, cashRegisterId: e.target.value })}
              >
                <option value="">Caja</option>
                {registers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {registerTypeLabel(r.type)} · {r.name || r.code}
                  </option>
                ))}
              </select>
              <select
                className="border rounded-md px-3 py-2"
                value={adjustForm.direction}
                onChange={(e) => setAdjustForm({ ...adjustForm, direction: e.target.value as 'IN' | 'OUT' })}
              >
                <option value="IN">Sobrante (+)</option>
                <option value="OUT">Faltante (−)</option>
              </select>
              <input
                required
                type="number"
                step="0.01"
                min="0.01"
                placeholder="Monto"
                className="border rounded-md px-3 py-2"
                value={adjustForm.amount}
                onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
              />
              <input
                required
                placeholder="Motivo"
                className="border rounded-md px-3 py-2"
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
              />
              <button type="submit" className="md:col-span-2 px-3 py-2 bg-slate-900 text-white rounded-md text-sm">
                Solicitar
              </button>
            </form>

            <ul className="space-y-2 text-sm">
              {adjustments.map((a) => (
                <li key={a.id} className="border rounded-md px-3 py-2 flex justify-between gap-3">
                  <span>
                    {a.status === 'PENDING'
                      ? 'Pendiente'
                      : a.status === 'REJECTED'
                        ? 'Rechazado'
                        : 'Aprobado'}{' '}
                    · {a.direction === 'IN' ? 'Sobrante' : 'Faltante'} · {formatMoney(Number(a.amount))} ·{' '}
                    {a.cashRegister?.code} · {a.reason}
                  </span>
                  {a.status === 'PENDING' && (
                    <button
                      type="button"
                      className="text-green-700 hover:underline"
                      onClick={async () => {
                        await apiService.approveTreasuryAdjustment(a.id);
                        await load();
                      }}
                    >
                      Aprobar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
